// Belege-Liste und Detailansicht (Suche, Vorschau, Erkennung, Metadaten, Status mit Nachweis-Historie, Bankzuordnung, Kontierung, DATEV) sowie die
// Verwaltung der Upload-Zugaenge (Smartphone der Ehefrau). Eingebettet in den Workflow-Container (BelegeWorkflow.js).
// Sichtbar nur fuer das freigegebene Admin-Konto (Server entscheidet); alle Pruefungen macht das Backend. Bankumsaetze werden nur GELESEN.
import { useState, useEffect, useCallback, useRef } from "react";
import THEME from "../../../theme";
import { fs } from "../utils/styles";
import { belegeApi, STATUS_NAME, BELEGARTEN, NAECHSTER_STATUS, belegeStufe } from "../../belege/belegeApi";
import { Chip, euro, datum, Meldung, Bestaetigung } from "../../belege/ui";
import { ErkennungBox, ZahlungBox, KontierungBox, DatevBox } from "../../belege/BelegeDetailTeile";
import BelegeFortschritt from "./belege/BelegeFortschritt";
import { Einklappbar, useBreite, BREAKPOINT_MITTEL } from "../ui/Bausteine";
import { zuletztGeoeffnet } from "../utils/zuletzt";

export function BelegeListe({ start, geheZu }) {
  const breite = useBreite();
  const kompakt = breite < BREAKPOINT_MITTEL;
  const sucheRef = useRef(null);
  const [liste, setListe] = useState(null);
  const [fehler, setFehler] = useState("");
  const [q, setQ] = useState(start?.q || "");
  const [status, setStatus] = useState(start?.status || "");
  const [von, setVon] = useState("");
  const [bis, setBis] = useState("");
  const [zugangFilter, setZugangFilter] = useState(start?.zugang || "");
  const [zugaenge, setZugaenge] = useState([]);
  const [offen, setOffen] = useState(null);       // Detail
  const [hinweis, setHinweis] = useState("");
  const dateiRef = useRef(null);
  const kameraRef = useRef(null);
  const offenIdRef = useRef(null);

  useEffect(() => { belegeApi.zugaenge().then(setZugaenge).catch(() => {}); }, []);

  const laden = useCallback(() => {
    setFehler("");
    belegeApi.liste({ q, status, von, bis, zugang_id: zugangFilter, limit: 100 }).then(setListe).catch(e => { setFehler(e.message); setListe({ belege: [], gesamt: 0, status_zaehler: {} }); });
  }, [q, status, von, bis, zugangFilter]);
  useEffect(() => { const t = setTimeout(laden, 250); return () => clearTimeout(t); }, [laden]);

  useEffect(() => { if (start?.fokus === "suche") sucheRef.current?.focus(); }, [start]);

  const oeffnen = useCallback(async (id) => {
    setHinweis("");
    try {
      const b = await belegeApi.detail(id);
      setOffen(b);
      zuletztGeoeffnet.merken({ typ: "beleg", id: b.id, titel: b.beleg_nr, untertitel: `${b.lieferant || "Lieferant fehlt"} · ${euro(b.brutto)}` });
    } catch (e) { setFehler(e.message); }
  }, []);
  useEffect(() => { if (start?.beleg) oeffnen(start.beleg); }, [start, oeffnen]);
  const aktualisieren = async (id) => { await oeffnen(id); laden(); };

  const hochladen = async (dateien, quelle = "upload") => {
    if (!dateien?.length) return;
    const fd = new FormData();
    Array.from(dateien).forEach(f => fd.append("files", f, f.name));
    fd.append("quelle", quelle);
    try {
      const r = await belegeApi.upload(fd);
      setHinweis(r.dublette ? `Bereits erfasst: ${r.beleg_nr}` : `Gespeichert als ${r.beleg_nr} – die Erkennung füllt die Felder gleich vor.`);
      laden();
      if (!r.dublette) { oeffnen(r.beleg_id); setTimeout(() => { if (offenIdRef.current === r.beleg_id) oeffnen(r.beleg_id); }, 4000); }   // zweiter Blick, wenn die Erkennung fertig ist
    } catch (e) { setFehler(e.message); }
  };

  const zaehler = liste?.status_zaehler || {};
  offenIdRef.current = offen ? offen.id : null;
  const filterFelder = (
    <>
      <div style={{ flex: "1 1 160px" }}>
        <label style={fs.label} htmlFor="bl-status">Status</label>
        <select id="bl-status" style={{ ...fs.input, width: "100%" }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Alle</option>
          {Object.entries(STATUS_NAME).filter(([k]) => belegeStufe.erweitert || !["an_datev_gesendet", "in_datev_bestaetigt"].includes(k)).map(([k, n]) => <option key={k} value={k}>{n}{zaehler[k] ? ` (${zaehler[k]})` : ""}</option>)}
        </select>
      </div>
      <div style={{ flex: "1 1 170px" }}>
        <label style={fs.label} htmlFor="bl-zugang">Eingang über</label>
        <select id="bl-zugang" style={{ ...fs.input, width: "100%" }} value={zugangFilter} onChange={e => setZugangFilter(e.target.value)}>
          <option value="">Alle</option>
          {zugaenge.map(z => <option key={z.id} value={z.id}>📱 {z.bezeichnung}{z.status === "widerrufen" ? " (widerrufen)" : ""}</option>)}
        </select>
      </div>
      <div style={{ flex: "0 1 140px" }}><label style={fs.label} htmlFor="bl-von">von</label><input id="bl-von" type="date" style={{ ...fs.input, width: "100%" }} value={von} onChange={e => setVon(e.target.value)} /></div>
      <div style={{ flex: "0 1 140px" }}><label style={fs.label} htmlFor="bl-bis">bis</label><input id="bl-bis" type="date" style={{ ...fs.input, width: "100%" }} value={bis} onChange={e => setBis(e.target.value)} /></div>
    </>
  );
  const aktiveFilter = [status, zugangFilter, von, bis].filter(Boolean).length;

  // Beleg-Detail als EIGENE Ansicht (statt unter bzw. neben der Liste); Filter und Suchbegriff der Liste bleiben beim Zurueckgehen erhalten
  if (offen) {
    return (
      <div>
        <Meldung fehler={fehler} hinweis={hinweis} />
        <DetailPanel key={offen.id} beleg={offen} kompakt={kompakt} onZurueck={() => setOffen(null)} onAenderung={() => aktualisieren(offen.id)} setFehler={setFehler} geheZu={geheZu} />
      </div>
    );
  }

  return (
    <div>
      <div style={{ ...fs.karte, padding: kompakt ? "1rem" : "1.75rem" }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: "2 1 220px" }}>
            <label style={fs.label} htmlFor="bl-suche">Suche (Lieferant, Rechnungsnr., Beleg-Nr., Dateiname, Betrag)</label>
            <input id="bl-suche" ref={sucheRef} style={{ ...fs.input, width: "100%", boxSizing: "border-box" }} value={q} onChange={e => setQ(e.target.value)} placeholder="z. B. Amazon, RE-2026, 119,00" />
          </div>
          {!kompakt && filterFelder}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input ref={dateiRef} type="file" multiple accept="image/*,.heic,.heif,application/pdf" style={{ display: "none" }} aria-label="Beleg-Datei wählen" onChange={e => { hochladen(e.target.files, "upload"); e.target.value = ""; }} />
            <input ref={kameraRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} aria-label="Beleg fotografieren" onChange={e => { hochladen(e.target.files, "kamera"); e.target.value = ""; }} />
            <button style={fs.btn} onClick={() => dateiRef.current?.click()}>+ Beleg hinzufügen</button>
            <button style={fs.btnGray} onClick={() => kameraRef.current?.click()}>📷 Foto</button>
          </div>
        </div>
        {kompakt && (
          <div style={{ marginTop: 10 }}>
            <Einklappbar titel="Filter" zusatz={aktiveFilter ? `${aktiveFilter} aktiv` : "keine aktiv"}>
              <div style={{ display: "grid", gap: 12 }}>{filterFelder}</div>
            </Einklappbar>
          </div>
        )}
      </div>

      <Meldung fehler={fehler} hinweis={hinweis} />

      <div>
        <div style={{ color: THEME.colors.textSecondary, marginBottom: 8 }}>{liste ? `${liste.gesamt} Beleg${liste.gesamt === 1 ? "" : "e"}` : "Lädt…"}</div>
        <div style={{ display: "grid", gap: 8, gridTemplateColumns: kompakt ? "1fr" : "repeat(auto-fill, minmax(340px, 1fr))" }}>
          {(liste?.belege || []).map(b => (
            <button key={b.id} onClick={() => oeffnen(b.id)} style={{ textAlign: "left", cursor: "pointer", background: THEME.colors.surface, minHeight: 72, minWidth: 0,
              border: `1px solid ${THEME.colors.border}`, borderRadius: THEME.radius.lg, padding: "12px 14px", color: THEME.colors.text }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                <strong>{b.beleg_nr}</strong><Chip status={b.status} />
              </div>
              <div style={{ marginTop: 4, overflowWrap: "anywhere" }}>{b.lieferant || <span style={{ color: THEME.colors.textMuted }}>Lieferant fehlt</span>}{b.rechnungsnr ? ` · ${b.rechnungsnr}` : ""}</div>
              <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginTop: 2 }}>
                {datum(b.rechnungsdatum || b.erfasst_am)} · {euro(b.brutto)} · {b.quelle}
                {b.eingang_ueber?.typ === "upload_zugang" && <span style={{ color: THEME.colors.accent }}> · 📱 {b.eingang_ueber.bezeichnung}</span>}
              </div>
            </button>
          ))}
          {liste && liste.belege.length === 0 && <div style={{ color: THEME.colors.textMuted }}>Keine Belege gefunden.</div>}
        </div>
      </div>
    </div>
  );
}

const ZUGANG_STATUS = { eingerichtet: ["Eingerichtet", THEME.colors.success], einladung_offen: ["Einladung offen", THEME.colors.warning], nicht_eingerichtet: ["Nicht eingerichtet", THEME.colors.textMuted],
  abgelaufen: ["Abgelaufen (lange nicht benutzt)", THEME.colors.warning], widerrufen: ["Widerrufen", THEME.colors.danger] };

// Upload-Zugaenge: reiner Upload fuer ein Smartphone (kein Konto, keine Admin-Rechte). Einrichtung per Link/QR-Code, der nur einmal angezeigt wird.
export function ZugaengePanel({ onFilter }) {
  const [zugaenge, setZugaenge] = useState([]);
  const [fehler, setFehler] = useState("");
  const [name, setName] = useState("");
  const [passwort, setPasswort] = useState("");       // erneute Authentifizierung des Admins (nie gespeichert)
  const [neuFuer, setNeuFuer] = useState(null);       // {z, passwort}: "Neu einrichten" verlangt ebenfalls das Passwort
  const [einrichtung, setEinrichtung] = useState(null);
  const laden = useCallback(() => { belegeApi.zugaenge().then(setZugaenge).catch(e => setFehler(e.message)); }, []);
  useEffect(() => { laden(); }, [laden]);
  const aktive = zugaenge.filter(z => z.status !== "widerrufen");

  const aktion = async (fn) => { setFehler(""); try { const r = await fn(); laden(); return r; } catch (e) { setFehler(e.message); } };
  const anlegen = async () => { const r = await aktion(() => belegeApi.zugangAnlegen(name, passwort)); setPasswort(""); if (r) { setEinrichtung({ ...r, titel: name }); setName(""); } };
  const neuBestaetigen = async () => { const { z, passwort: pw } = neuFuer; const r = await aktion(() => belegeApi.zugangNeuEinrichten(z.id, pw)); setNeuFuer(null); if (r) setEinrichtung({ ...r, titel: z.bezeichnung }); };
  const alleSperren = async () => { if (!window.confirm("ALLE Upload-Zugänge sofort sperren? (z. B. bei verlorenem Handy) Danach muss jedes Gerät neu eingerichtet werden.")) return; await aktion(() => belegeApi.zugaengeAlleWiderrufen()); };
  const widerrufen = async (z) => { if (!window.confirm(`Upload-Zugang „${z.bezeichnung}“ widerrufen? Das Gerät kann danach nichts mehr hochladen.`)) return; await aktion(() => belegeApi.zugangWiderrufen(z.id)); };

  return (
    <div style={{ ...fs.karte, padding: "1rem 1.25rem" }}>
      <strong>📱 Upload-Zugänge (Smartphone nur zum Hochladen){aktive.length ? ` · ${aktive.length} aktiv` : ""}</strong>
      <div style={{ marginTop: 12 }}>
        <Meldung fehler={fehler} />
        <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginBottom: 10 }}>
          Ein Upload-Zugang kann nur Belege hochladen und die eigenen Belegnummern sehen – kein Benutzerkonto, kein Finanzmodul, kein Ansehen oder Löschen fremder Belege.
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          {zugaenge.map(z => { const [t, f] = ZUGANG_STATUS[z.status] || [z.status, THEME.colors.textMuted]; return (
            <div key={z.id} style={{ border: `1px solid ${THEME.colors.border}`, borderRadius: THEME.radius.md, padding: "10px 12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <strong>{z.bezeichnung}</strong>
                <span style={{ border: `1px solid ${f}`, color: f, borderRadius: 999, padding: "2px 10px", fontSize: THEME.fontSize.sm }}>{t}</span>
              </div>
              {z.status === "widerrufen" && z.widerruf_grund && (
                <div style={{ color: z.verdacht ? THEME.colors.danger : THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginTop: 4 }}>{z.verdacht ? "⚠ " : ""}{z.widerruf_grund}</div>
              )}
              <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm, marginTop: 4 }}>
                {z.geraet ? `${z.geraet} · ` : ""}{z.uploads_gesamt} Upload{z.uploads_gesamt === 1 ? "" : "s"}
                {z.letzter_upload ? ` · letzter ${new Date(z.letzter_upload).toLocaleString("de-DE")}` : ""}
                {z.letzte_nutzung ? ` · zuletzt aktiv ${new Date(z.letzte_nutzung).toLocaleString("de-DE")}` : ""}
              </div>
              {z.status !== "widerrufen" && (
                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                  {onFilter && <button style={fs.btnGray} onClick={() => onFilter(String(z.id))}>Belege dieses Zugangs</button>}
                  <button style={fs.btnGray} onClick={() => setNeuFuer({ z, passwort: "" })}>Neu einrichten</button>
                  <button style={fs.btnDanger} onClick={() => widerrufen(z)}>Widerrufen</button>
                </div>
              )}
            </div>); })}
          {zugaenge.length === 0 && <div style={{ color: THEME.colors.textMuted }}>Noch kein Upload-Zugang eingerichtet.</div>}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <input style={{ ...fs.input, flex: "1 1 200px" }} placeholder="Bezeichnung, z. B. Katharina – iPhone" value={name} onChange={e => setName(e.target.value)} maxLength={60} />
          <input type="password" autoComplete="current-password" style={{ ...fs.input, flex: "1 1 200px" }} placeholder="Dein Passwort zur Bestätigung" value={passwort} onChange={e => setPasswort(e.target.value)} />
          <button style={fs.btn} disabled={name.trim().length < 3 || !passwort} onClick={anlegen}>+ Smartphone-Zugang einrichten</button>
        </div>
        <div style={{ marginTop: 12 }}>
          <button style={fs.btnDanger} onClick={alleSperren}>🛑 Notbremse: alle Upload-Zugänge sofort sperren</button>
        </div>
        <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 10 }}>
          Schutz: neue Einladungen nur mit deinem Passwort · Einladung 30 Minuten, nur einmal · Cookie wird alle 24 h erneuert; ein kopiertes altes Cookie sperrt den Zugang automatisch · Ablauf nach 90 Tagen ohne Nutzung · höchstens 40 Uploads pro Stunde.
        </div>
      </div>
      {neuFuer && (
        <div role="dialog" aria-label="Neu einrichten" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
          <div style={{ background: THEME.colors.surface, borderRadius: THEME.radius.xl, padding: 24, maxWidth: 380, width: "100%" }}>
            <h3 style={{ marginTop: 0 }}>„{neuFuer.z.bezeichnung}“ neu einrichten</h3>
            <p style={{ color: THEME.colors.textSecondary }}>Das bisherige Gerät verliert sofort den Zugang. Zur Bestätigung bitte dein Passwort eingeben.</p>
            <input type="password" autoComplete="current-password" autoFocus style={{ ...fs.input, width: "100%", boxSizing: "border-box" }} value={neuFuer.passwort} onChange={e => setNeuFuer({ ...neuFuer, passwort: e.target.value })} />
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button style={fs.btn} disabled={!neuFuer.passwort} onClick={neuBestaetigen}>Neu einrichten</button>
              <button style={fs.btnGray} onClick={() => setNeuFuer(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
      {einrichtung && (
        <div role="dialog" aria-label="Einrichtung" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
          <div style={{ background: THEME.colors.surface, borderRadius: THEME.radius.xl, padding: 24, maxWidth: 420, width: "100%", maxHeight: "92vh", overflow: "auto" }}>
            <h3 style={{ marginTop: 0 }}>Einrichtung: {einrichtung.titel}</h3>
            <p style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.base }}>
              Auf dem Smartphone <b>diesen QR-Code mit der Kamera scannen</b> (oder den Link dorthin senden). Dann „Zum Home-Bildschirm“ und die App <b>„ComeFit Belege“</b> öffnen.
              Der Link gilt nur <b>{einrichtung.gueltig_minuten} Minuten</b> und nur <b>einmal</b> – er wird hier nur jetzt angezeigt.
            </p>
            <img alt="QR-Code zur Einrichtung" src={`data:image/png;base64,${einrichtung.qr_png_base64}`} style={{ width: 240, height: 240, background: "#fff", padding: 8, borderRadius: 12, display: "block", margin: "0 auto" }} />
            <input readOnly value={einrichtung.einrichtungs_url} onFocus={e => e.target.select()} style={{ ...fs.input, width: "100%", boxSizing: "border-box", marginTop: 12, fontSize: 12 }} />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button style={fs.btnGray} onClick={() => navigator.clipboard?.writeText(einrichtung.einrichtungs_url)}>Link kopieren</button>
              <button style={fs.btn} onClick={() => setEinrichtung(null)}>Fertig – Fenster schließen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Ein Abschnitt der Detailansicht: Desktop = feste Ueberschrift, Smartphone = einklappbar (kein langes Scrollen)
function Teil({ titel, zusatz, kompakt, offenStart = true, children }) {
  if (kompakt) return <Einklappbar titel={titel} zusatz={zusatz} offenStart={offenStart}>{children}</Einklappbar>;
  return (
    <section style={{ background: THEME.colors.surface, borderRadius: THEME.radius.xl, padding: "16px 18px", marginBottom: 16, minWidth: 0 }}>
      <h3 style={{ margin: "0 0 10px", fontSize: THEME.fontSize.lg }}>{titel}{zusatz ? <span style={{ color: THEME.colors.textSecondary, fontWeight: 400, marginLeft: 8, fontSize: THEME.fontSize.sm }}>{zusatz}</span> : null}</h3>
      {children}
    </section>
  );
}

// Beleg-Detail als eigene Ansicht: Belegvorschau, Rechnungsinformationen, zugeordneter Bankumsatz, Bearbeitung, Status. Alle bisherigen Funktionen bleiben erhalten.
function DetailPanel({ beleg, kompakt, onZurueck, onAenderung, setFehler, geheZu }) {
  const [f, setF] = useState({
    lieferant: beleg.lieferant || "", rechnungsnr: beleg.rechnungsnr || "", rechnungsdatum: beleg.rechnungsdatum || "",
    netto: beleg.netto ?? "", ust: beleg.ust ?? "", brutto: beleg.brutto ?? "", belegart: beleg.belegart || "", zahlungsart: beleg.zahlungsart || "", notiz: beleg.notiz || "",
  });
  const [grund, setGrund] = useState("");
  const [abweichungFrage, setAbweichungFrage] = useState(null);   // Vorschlag mit Differenz: nur nach manueller Pruefung
  const gesperrt = ["an_datev_gesendet", "in_datev_bestaetigt"].includes(beleg.status);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const aktion = async (fn) => { setFehler(""); try { await fn(); await onAenderung(); } catch (e) { setFehler(e.message); } };
  const speichern = () => aktion(() => belegeApi.aendern(beleg.id, { ...f, netto: String(f.netto), ust: String(f.ust), brutto: String(f.brutto) }));
  const weiter = NAECHSTER_STATUS[beleg.status];
  const statusWechsel = (neu, extra = {}) => aktion(() => belegeApi.statusSetzen(beleg.id, { status: neu, bemerkung: grund || undefined, ...extra }));
  const dublette = beleg.dublette_kandidaten || [];
  const differenz = (z) => (beleg.brutto !== null ? Math.round((beleg.brutto - z.betrag_anteil) * 100) / 100 : 0);
  const bestaetigeVorschlag = (z) => {
    if (Math.abs(differenz(z)) > 0.005) { setAbweichungFrage({ z, modus: "akzeptieren" }); return undefined; }   // Differenz -> manuelle Pruefung, nie stillschweigend
    return aktion(() => belegeApi.bankBestaetigen(beleg.id, [{ bankumsatz_id: z.umsatz.id, betrag_anteil: z.betrag_anteil }]));
  };
  const zuordnungen = beleg.bankzuordnungen.filter(z => z.art !== "verworfen");
  const bestaetigt = zuordnungen.filter(z => z.art === "bestaetigt").length;
  const hoehe = kompakt ? 360 : 640;

  const vorschau = (
    <Teil titel="Belegvorschau" kompakt={kompakt}>
      <iframe title={`Vorschau ${beleg.beleg_nr}`} src={`${belegeApi.dateiUrl(beleg.id)}#toolbar=0&navpanes=0&view=FitH`} style={{ width: "100%", height: hoehe, border: `1px solid ${THEME.colors.border}`, borderRadius: THEME.radius.md, background: "#fff" }} />
      <div style={{ fontSize: THEME.fontSize.sm, color: THEME.colors.textMuted, marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <a style={{ color: THEME.colors.primary, minHeight: 48, display: "inline-flex", alignItems: "center" }} href={belegeApi.dateiUrl(beleg.id)} target="_blank" rel="noreferrer">Vorschau im ganzen Fenster öffnen</a>
        {beleg.originale.length > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>Originale: {beleg.originale.map(o => <a key={o.datei_id} style={{ color: THEME.colors.primary, minHeight: 48, display: "inline-flex", alignItems: "center" }} href={belegeApi.originalUrl(beleg.id, o.datei_id)} target="_blank" rel="noreferrer">{o.dateiname || `Datei ${o.datei_id}`}</a>)}</span>}
      </div>
    </Teil>
  );

  const rechnung = (
    <Teil titel="Rechnungsinformationen" kompakt={kompakt}>
      <ErkennungBox beleg={beleg} onAenderung={onAenderung} setFehler={setFehler} />
      {dublette.length > 0 && (
        <div style={{ border: `1px solid ${THEME.colors.warning}`, color: THEME.colors.warning, borderRadius: THEME.radius.md, padding: 10, marginTop: 12 }}>
          Mögliche Dublette: {dublette.map(d => `${d.beleg_nr} (${d.grund})`).join("; ")}
          {beleg.status === "eingegangen" && <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={fs.btnGray} onClick={() => statusWechsel("dublette", { dublette_von: dublette[0].id })}>Als Dublette von {dublette[0].beleg_nr} markieren</button>
            <button style={fs.btnGray} onClick={() => statusWechsel("geprueft", { keine_dublette_bestaetigt: true })}>Keine Dublette – prüfen</button>
          </div>}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginTop: 14 }}>
        {[["lieferant", "Lieferant"], ["rechnungsnr", "Rechnungsnr."], ["rechnungsdatum", "Rechnungsdatum", "date"], ["netto", "Netto"], ["ust", "USt"], ["brutto", "Brutto"], ["zahlungsart", "Zahlungsart"]].map(([k, l, t]) => (
          <div key={k}><label style={fs.label} htmlFor={`bd-${k}`}>{l}</label><input id={`bd-${k}`} type={t || "text"} disabled={gesperrt} style={{ ...fs.input, width: "100%", boxSizing: "border-box" }} value={f[k]} onChange={set(k)} /></div>
        ))}
        <div><label style={fs.label} htmlFor="bd-belegart">Belegart</label>
          <select id="bd-belegart" disabled={gesperrt} style={{ ...fs.input, width: "100%" }} value={f.belegart} onChange={set("belegart")}><option value="">–</option>{BELEGARTEN.map(([k, n]) => <option key={k} value={k}>{n}</option>)}</select></div>
      </div>
      <label style={{ ...fs.label, marginTop: 10 }} htmlFor="bd-notiz">Notiz</label>
      <textarea id="bd-notiz" rows={Math.min(6, Math.max(2, (f.notiz.match(/\n/g) || []).length + 1))} style={{ ...fs.input, width: "100%", boxSizing: "border-box", resize: "vertical" }} value={f.notiz} onChange={set("notiz")} />
      <button style={{ ...fs.btn, marginTop: 10, minHeight: 48 }} onClick={speichern}>Angaben speichern</button>
    </Teil>
  );

  const bank = (
    <Teil titel="Zugeordneter Bankumsatz" zusatz={bestaetigt ? `${bestaetigt} bestätigt` : zuordnungen.length ? "Vorschlag" : "keiner"} kompakt={kompakt} offenStart={zuordnungen.length > 0 || !kompakt}>
      {!gesperrt && <button style={{ ...fs.btnGray, minHeight: 48 }} onClick={() => aktion(() => belegeApi.abgleich(beleg.id))}>Vorschläge aus den Bankumsätzen berechnen</button>}
      <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
        {zuordnungen.map(z => (
          <div key={z.id} style={{ border: `1px solid ${z.art === "bestaetigt" ? THEME.colors.success : THEME.colors.border}`, borderRadius: THEME.radius.md, padding: 10 }}>
            <div style={{ overflowWrap: "anywhere" }}>{z.art === "bestaetigt" ? "✔ Bestätigt" : `Vorschlag (Score ${z.score})`} · {datum(z.umsatz.datum)} · {euro(z.umsatz.betrag)} · {z.umsatz.partner || "–"}{z.art === "bestaetigt" && Math.abs(Math.abs(z.umsatz.betrag) - z.betrag_anteil) > 0.005 ? ` · davon ${euro(z.betrag_anteil)}` : ""}</div>
            <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, overflowWrap: "anywhere" }}>{z.umsatz.verwendungszweck}{z.umsatz.kategorie ? ` · Kategorie: ${z.umsatz.kategorie}` : ""}</div>
            {z.begruendung && <div style={{ color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>{z.begruendung}</div>}
            {z.art === "vorschlag" && Math.abs(differenz(z)) > 0.005 && <div style={{ color: THEME.colors.warning, marginTop: 4 }}>⚠ Abweichung: Rechnung {euro(beleg.brutto)} · Bankumsatz {euro(z.betrag_anteil)} · Differenz {euro(differenz(z))} – bitte prüfen</div>}
            {z.art === "vorschlag" && !gesperrt && <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={fs.btn} onClick={() => bestaetigeVorschlag(z)}>{Math.abs(differenz(z)) > 0.005 ? "Differenz prüfen…" : "Bestätigen"}</button>
              {Math.abs(differenz(z)) > 0.005 && differenz(z) > 0 && <button style={fs.btnGray} onClick={() => setAbweichungFrage({ z, modus: "teil" })}>Als Teilzahlung zuordnen</button>}
              <button style={fs.btnGray} onClick={() => aktion(() => belegeApi.bankVerwerfen(beleg.id, z.umsatz.id))}>Verwerfen</button>
            </div>}
          </div>
        ))}
        {zuordnungen.length === 0 && <div style={{ color: THEME.colors.textMuted }}>Noch keine Zuordnung.</div>}
      </div>
      <ZahlungBox beleg={beleg} onAenderung={onAenderung} setFehler={setFehler} />
    </Teil>
  );

  const statusBlock = (
    <Teil titel="Status" zusatz={STATUS_NAME[beleg.status] || beleg.status} kompakt={kompakt} offenStart={!kompakt || beleg.status === "eingegangen" || beleg.status === "geprueft"}>
      <input style={{ ...fs.input, width: "100%", boxSizing: "border-box" }} aria-label="Begründung oder Bemerkung" placeholder="Begründung / Bemerkung (bei Ablehnung, Rückfrage, Zurückstufen, 'ohne Bankumsatz')" value={grund} onChange={e => setGrund(e.target.value)} />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
        {weiter === "geprueft" && <button style={fs.btn} onClick={() => statusWechsel("geprueft")}>✔ Als geprüft markieren</button>}
        {weiter === "bankumsatz_zugeordnet" && <>
          <button style={fs.btn} onClick={() => statusWechsel("bankumsatz_zugeordnet")}>✔ Bankumsatz ist zugeordnet</button>
          <button style={fs.btnGray} onClick={() => statusWechsel("bankumsatz_zugeordnet", { ohne_bankumsatz_grund: grund })}>Ohne Bankumsatz (Begründung nötig)</button>
        </>}
        {!gesperrt && beleg.status !== "eingegangen" && !["abgelehnt", "dublette", "rueckfrage"].includes(beleg.status) && <button style={fs.btnGray} onClick={() => statusWechsel(beleg.status === "geprueft" ? "eingegangen" : "geprueft")}>↩ Zurückstufen</button>}
        {!gesperrt && ["eingegangen", "geprueft"].includes(beleg.status) && <>
          <button style={fs.btnGray} onClick={() => statusWechsel("rueckfrage")}>Rückfrage</button>
          <button style={fs.btnDanger} onClick={() => statusWechsel("abgelehnt")}>Ablehnen</button>
        </>}
        {["abgelehnt", "dublette", "rueckfrage"].includes(beleg.status) && <button style={fs.btnGray} onClick={() => statusWechsel("eingegangen")}>Wieder öffnen</button>}
      </div>
      {belegeStufe.erweitert && <div style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm, marginTop: 8 }}>
        „An DATEV gesendet“ und „In DATEV bestätigt“ werden nur über die DATEV-Übergabe bzw. ein archiviertes Nachweis-Dokument gesetzt – nie per Klick.
      </div>}
      <ol style={{ margin: "10px 0 0", paddingLeft: 18, color: THEME.colors.textSecondary, fontSize: THEME.fontSize.sm }}>
        {beleg.historie.map((h, i) => <li key={i}>{new Date(h.zeitpunkt).toLocaleString("de-DE")} · {STATUS_NAME[h.nach] || h.nach}{h.zugang_id ? " (über Upload-Zugang)" : ""}{h.nachweis_typ ? ` (Nachweis: ${h.nachweis_typ})` : ""}{h.bemerkung ? ` – ${h.bemerkung}` : ""}</li>)}
      </ol>
    </Teil>
  );

  const weitere = (
    <Teil titel="Kontierung und DATEV" kompakt={kompakt} offenStart={false}>
      <KontierungBox beleg={beleg} onAenderung={onAenderung} setFehler={setFehler} />
      <DatevBox beleg={beleg} onAenderung={onAenderung} setFehler={setFehler} geheZu={geheZu} />
    </Teil>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <button type="button" style={{ ...fs.btnGray, minHeight: 48 }} onClick={onZurueck}>← Zurück zur Belegliste</button>
        <h2 style={{ margin: 0, fontSize: THEME.fontSize.lg, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>{beleg.beleg_nr} <Chip status={beleg.status} /></h2>
      </div>
      <BelegeFortschritt beleg={beleg} />
      <div style={{ margin: "8px 0 14px", fontSize: THEME.fontSize.sm, color: beleg.eingang_ueber?.typ === "upload_zugang" ? THEME.colors.accent : THEME.colors.textMuted }}>
        Eingang über: {beleg.eingang_ueber?.typ === "upload_zugang" ? `📱 Upload-Zugang „${beleg.eingang_ueber.bezeichnung}“` : beleg.quelle === "imap" ? "✉ Rechnungspostfach" : beleg.quelle === "amazon" ? "Amazon-Import" : "Verwaltung (Admin-Konto)"} · {datum(beleg.erfasst_am)}
      </div>

      {kompakt ? (
        <>{vorschau}{rechnung}{bank}{statusBlock}{weitere}</>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16, alignItems: "start" }}>
          <div style={{ position: "sticky", top: 8, minWidth: 0 }}>{vorschau}</div>
          <div style={{ minWidth: 0 }}>{rechnung}{bank}{statusBlock}{weitere}</div>
        </div>
      )}

      {abweichungFrage && abweichungFrage.modus === "akzeptieren" && (
        <Bestaetigung titel="Abweichung manuell prüfen" grundPflicht={5} okText="Differenz akzeptieren"
          text={`Rechnungsbetrag ${euro(beleg.brutto)}\nBankumsatz ${euro(abweichungFrage.z.betrag_anteil)}\nDifferenz ${euro(differenz(abweichungFrage.z))}\n\nDie Differenz wird mit deiner Begründung am Beleg festgehalten. Kommt der Rest noch, wähle stattdessen „Als Teilzahlung zuordnen“.`}
          onAbbruch={() => setAbweichungFrage(null)}
          onOk={({ grund: g }) => { const z = abweichungFrage.z; setAbweichungFrage(null); aktion(() => belegeApi.bankBestaetigen(beleg.id, [{ bankumsatz_id: z.umsatz.id, betrag_anteil: z.betrag_anteil }], { abweichung_grund: g })); }} />
      )}
      {abweichungFrage && abweichungFrage.modus === "teil" && (
        <Bestaetigung titel="Als Teilzahlung zuordnen?" okText="Als Teilzahlung zuordnen"
          text={`Der Bankumsatz (${euro(abweichungFrage.z.betrag_anteil)}) wird als Teilzahlung gebucht. Es bleiben ${euro(differenz(abweichungFrage.z))} offen, bis die Restzahlung zugeordnet ist.`}
          onAbbruch={() => setAbweichungFrage(null)}
          onOk={() => { const z = abweichungFrage.z; setAbweichungFrage(null); aktion(() => belegeApi.bankBestaetigen(beleg.id, [{ bankumsatz_id: z.umsatz.id, betrag_anteil: z.betrag_anteil }], { teilweise: true, ergaenze: true })); }} />
      )}
    </div>
  );
}

export default BelegeListe;
